import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import svgPaths from "../../imports/svg-0oqr7j0o96";
import { PrimaryButton } from './PrimaryButton';
import { getMe, resendVerification } from '../../lib/professional-api';
import { getToken, getUser, patchUser } from '../../lib/auth';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

// Substring fingerprint of the "this email is already verified" message
// the backend emits as HTTP 400 from /professional-auth/resend-
// verification (i18n key `messages.superAdmin.professional.verifyEmail
// .alreadyVerified`). We pair status===400 + substring + token-presence
// before redirecting, so an unrelated 400 (e.g. future validation
// error) can't silently route someone past RequireVerifiedPro.
// English-only as of 2026-06: when other locales ship, replace this
// with a stable backend `error_code` field instead of pattern-matching.
const ALREADY_VERIFIED_HINTS = ['already verified', 'already been verified'];

function looksAlreadyVerified(message: string | null | undefined): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return ALREADY_VERIFIED_HINTS.some((hint) => lower.includes(hint));
}

export function ProSignUpSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  // Pull the email from the navigation state (set by the signup form) and
  // fall back to whichever auth user is currently in localStorage so a
  // refresh of the success page still works.
  const stateEmail = (location.state as { email?: string } | null)?.email;
  const email = stateEmail || getUser()?.email || '';

  const [resending, setResending] = useState(false);
  // `alreadyVerifiedAnon` is set when an anonymous visitor (no token)
  // tries to resend an email that's already verified. We can't redirect
  // them into the app, so we surface a persistent inline Sign-in CTA
  // instead — the brief toast alone is too easy to miss.
  const [alreadyVerifiedAnon, setAlreadyVerifiedAnon] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error(
        'We need your email to resend the verification — try signing up again.',
      );
      return;
    }
    setResending(true);
    setAlreadyVerifiedAnon(false);
    try {
      const res = await resendVerification(email);
      // Guard against a leaked i18n key (e.g. "messages.superAdmin.
      // professional.verifyEmail.resentIfEligible" when the backend's i18n
      // didn't resolve) — never toast a raw key at the user.
      toast.success(
        res.message && !res.message.startsWith('messages.')
          ? res.message
          : 'If that email is on file and not yet verified, a new verification link is on its way.',
      );
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not resend right now.');
      const status = (err as any)?.response?.status;
      // Already-verified detection: backend returns 400 with the
      // "already verified" message. Status alone isn't enough (400 is
      // also reachable on validation failures) — require BOTH 400 AND
      // a substring match.
      const isAlreadyVerified = status === 400 && looksAlreadyVerified(msg);

      if (isAlreadyVerified && getToken()) {
        // Logged-in pro on a stale tab. Redirect them where the rest of
        // the app would route them. Pull canonical fields from
        // /professional/me so the cache is fresh, then navigate.
        toast.success("You're already verified — taking you in.");
        try {
          const me = await getMe();
          // Mid-flight logout safety: if the pro hit Logout while
          // we were awaiting, getToken() is now null. Don't navigate
          // to a protected route on a cleared session — the auth
          // subscriber that cleared the token will drive its own
          // redirect; we just bail silently.
          if (!getToken()) return;
          const info: any = me?.info ?? {};
          const u = info.user ?? info;
          // Only patch when the server actually returned a real value.
          // Synthesising a fabricated timestamp here would corrupt the
          // cache if the backend ever ships a regression that leaks
          // "already verified" copy to a still-unverified flow.
          if (u?.email_verified_at) {
            patchUser({
              status: u?.status,
              email_verified_at: u.email_verified_at,
            });
          }
          navigate(
            info.onboarding_completed_at
              ? '/professional/dashboard'
              : '/professional/service-offer',
            { replace: true },
          );
        } catch {
          // Couldn't refresh — same logout guard applies (silent
          // bail: the logout subscriber drives the redirect), then
          // route to /service-offer (NOT /dashboard) which has a
          // softer guard and avoids a triple-hop on slow networks.
          if (!getToken()) return;
          navigate('/professional/service-offer', { replace: true });
        }
        return;
      }

      if (isAlreadyVerified) {
        // Anonymous visitor (no token) — can't redirect, just guide
        // them to Sign in via an inline CTA below.
        toast.success('Your email is already verified — please sign in.');
        setAlreadyVerifiedAnon(true);
        return;
      }

      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full max-w-[420px] flex flex-col items-center text-center">
      <div className="relative w-[181px] h-[125px] mb-8">
        <svg className="absolute inset-0 w-full h-[124px]" fill="none" preserveAspectRatio="none" viewBox="0 0 181 124">
          <g>
            <path d={svgPaths.pab57f00} fill="white" />
            <path d={svgPaths.pab57f00} fill="#3A3335" fillOpacity="0.04" />
          </g>
        </svg>
        <div className="absolute left-[29.28%] right-[30.39%] top-[41.6%] bottom-0">
          <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 73 73">
            <circle cx="36.5" cy="36.5" fill="#C6D8D3" r="36.5" />
          </svg>
        </div>
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-[12.5px] material-icons text-[28px] text-white">
          {''}
        </span>
      </div>

      <h1 className="font-['Playfair_Display',sans-serif] font-extrabold leading-[48px] text-[#8e2a2f] text-[34px] tracking-[-0.0944px] mb-2">
        Thank you, Pro!
      </h1>

      <div className="font-['DM_Sans',sans-serif] font-normal text-[#908e8e] text-[14px] tracking-[-0.1px] mb-8" style={{ fontVariationSettings: "'opsz' 14" }}>
        <p className="leading-[21px]">
          We sent a verification email to{' '}
          <span className="text-[#3a3335] font-bold">{email || 'your inbox'}</span>.
        </p>
        <p className="leading-[21px]">
          Click the button in that email to activate your account and continue.
        </p>
      </div>

      <div className="w-full flex flex-col gap-3">
        <PrimaryButton
          onClick={handleResend}
          className="w-full py-4"
          disabled={resending}
        >
          {resending ? 'Sending…' : "Didn't get it? Resend email"}
        </PrimaryButton>

        {alreadyVerifiedAnon ? (
          <p
            role="status"
            aria-live="polite"
            className="text-[#3a3335] text-[13px]"
          >
            Your email is already verified.{' '}
            <Link
              to="/professional/signin"
              className="text-[#8e2a2f] font-bold underline rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8e2a2f]"
            >
              Sign in
            </Link>{' '}
            to continue.
          </p>
        ) : null}
      </div>
    </div>
  );
}
